import { fetchConfig, debounce } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Auto-Geschenk
 *
 * Keeps a configured 0€ gift variant in the cart whose quantity mirrors the total
 * quantity of all "trigger" products (products that belong to a configured
 * collection). The gift is added when a trigger product enters the cart, its
 * quantity is synced, and it is removed once no trigger products remain.
 *
 * Config is injected by snippets/auto-gift.liquid as a JSON script tag.
 */

/**
 * @typedef {Object} AutoGiftConfig
 * @property {number} giftVariantId - Variant id of the free gift.
 * @property {string} badgeLabel - Label shown in the cart (unused here, rendered in liquid).
 * @property {number[]} triggerProductIds - Product ids that qualify for the gift.
 */

/** @returns {AutoGiftConfig | null} */
function readConfig() {
  const el = document.getElementById('auto-gift-config');
  if (!el?.textContent) return null;
  try {
    const config = JSON.parse(el.textContent);
    if (!config.giftVariantId || !Array.isArray(config.triggerProductIds)) return null;
    return config;
  } catch (_) {
    return null;
  }
}

const config = readConfig();

if (config) {
  const triggerIds = new Set(config.triggerProductIds.map(Number));
  let isSyncing = false;

  /** Collects the section ids of every cart-items component currently in the DOM. */
  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter((id, index, all) => id && all.indexOf(id) === index);

  /** @returns {Promise<any|null>} the current cart object, or null on failure */
  const getCart = async () => {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`);
      return res.ok ? await res.json() : null;
    } catch (_) {
      return null;
    }
  };

  /** @param {any} item */
  const isGiftLine = (item) =>
    item.variant_id === config.giftVariantId && item.properties?._gift === 'true';

  /**
   * Reconciles the gift line with the current trigger quantity.
   * Idempotent: if the gift quantity already matches, nothing is mutated.
   */
  const syncGift = async () => {
    if (isSyncing) return;
    isSyncing = true;

    try {
      const cart = await getCart();
      if (!cart) return;

      const giftLine = cart.items.find(isGiftLine);
      const triggerQty = cart.items
        .filter((item) => !isGiftLine(item) && triggerIds.has(Number(item.product_id)))
        .reduce((sum, item) => sum + item.quantity, 0);

      const currentQty = giftLine?.quantity ?? 0;
      if (triggerQty === currentQty) return; // already in sync, avoids loops

      const sections = cartSectionIds();
      let mutationResponse;

      if (giftLine) {
        // Line items with properties must be updated via their line key, not variant id.
        const body = JSON.stringify({
          id: giftLine.key,
          quantity: triggerQty,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });
        mutationResponse = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body })).then((r) =>
          r.json()
        );
      } else if (triggerQty > 0) {
        // Add the gift, marked so fulfillment/backend can recognise it.
        const body = JSON.stringify({
          items: [{ id: config.giftVariantId, quantity: triggerQty, properties: { _gift: 'true' } }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });
        mutationResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body })).then((r) => r.json());
      }

      // Authoritative cart for the count + let cart components re-render from sections.
      const updatedCart = await getCart();
      if (!updatedCart) return;

      document.dispatchEvent(
        new CartUpdateEvent(updatedCart, 'auto-gift', {
          source: 'auto-gift',
          itemCount: updatedCart.item_count,
          sections: mutationResponse?.sections,
        })
      );
    } finally {
      isSyncing = false;
    }
  };

  const debouncedSync = debounce(syncGift, 100);

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    // Ignore our own updates to prevent a feedback loop.
    if (event.detail?.data?.source === 'auto-gift') return;
    debouncedSync();
  });

  // Reconcile on load (e.g. trigger product already in cart from a previous visit).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGift);
  } else {
    syncGift();
  }
}
